/**
 * Enhanced Treatment Plan Component
 * Premium UX, Production-Grade, Therapist/Psychiatrist Focused
 * Comprehensive treatment planning with goals, interventions, progress tracking
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
  Stack,
  Checkbox,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CheckCircle,
  RadioButtonUnchecked,
  TrendingUp,
  CalendarToday,
  Psychology,
  LocalHospital,
  Medication,
  Assessment,
  Assignment,
  Goal,
  History,
  Download,
  Print,
  Share,
  ExpandMore,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { format, parseISO, addDays, addWeeks, addMonths, subMonths } from 'date-fns';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const GoalCard = styled(Paper)(({ theme, completed }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `2px solid ${completed 
    ? alpha(theme.palette.success.main, 0.3)
    : alpha(theme.palette.primary.main, 0.2)}`,
  background: completed
    ? alpha(theme.palette.success.main, 0.05)
    : alpha(theme.palette.background.paper, 0.8),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[4],
  },
}));

const EnhancedTreatmentPlan = ({ patientId: propPatientId, role = 'doctor' }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { id: paramPatientId } = useParams();
  const finalPatientId = propPatientId || paramPatientId;

  // Local state
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [planFormData, setPlanFormData] = useState({
    title: '',
    description: '',
    diagnosis: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: '',
    status: 'active',
    priority: 'normal',
  });
  const [goalFormData, setGoalFormData] = useState({
    title: '',
    description: '',
    targetDate: '',
    status: 'not_started',
    priority: 'normal',
    interventions: [],
    outcomeMeasures: '',
    progress: 0,
  });

  // Mock data - replace with actual API calls
  const treatmentPlans = useMemo(() => {
    return plans.length > 0 ? plans : [
      {
        id: 1,
        title: 'Depression Treatment Plan',
        description: 'Comprehensive treatment plan for major depressive disorder',
        diagnosis: 'Major Depressive Disorder (F32.1)',
        startDate: format(subMonths(new Date(), 2), 'yyyy-MM-dd'),
        endDate: format(addMonths(new Date(), 4), 'yyyy-MM-dd'),
        status: 'active',
        priority: 'high',
        goals: [
          {
            id: 1,
            title: 'Reduce depressive symptoms by 50%',
            description: 'Target PHQ-9 score reduction from baseline',
            targetDate: format(addWeeks(new Date(), 8), 'yyyy-MM-dd'),
            status: 'in_progress',
            priority: 'high',
            progress: 45,
            interventions: ['CBT sessions', 'Medication management', 'Exercise program'],
            outcomeMeasures: 'PHQ-9 score < 10',
            completed: false,
          },
          {
            id: 2,
            title: 'Improve sleep quality',
            description: 'Achieve consistent 7-8 hours of sleep per night',
            targetDate: format(addWeeks(new Date(), 4), 'yyyy-MM-dd'),
            status: 'in_progress',
            priority: 'medium',
            progress: 60,
            interventions: ['Sleep hygiene education', 'Melatonin supplementation'],
            outcomeMeasures: 'Sleep diary shows improvement',
            completed: false,
          },
          {
            id: 3,
            title: 'Increase social engagement',
            description: 'Participate in social activities at least twice per week',
            targetDate: format(addMonths(new Date(), 3), 'yyyy-MM-dd'),
            status: 'not_started',
            priority: 'medium',
            progress: 20,
            interventions: ['Social skills training', 'Group therapy'],
            outcomeMeasures: 'Self-reported social activity log',
            completed: false,
          },
        ],
      },
    ];
  }, [plans]);

  const currentPlan = activePlan || treatmentPlans.find(p => p.status === 'active') || treatmentPlans[0];

  // Calculate overall progress
  const overallProgress = useMemo(() => {
    if (!currentPlan || !currentPlan.goals || currentPlan.goals.length === 0) return 0;
    const totalProgress = currentPlan.goals.reduce((sum, goal) => sum + (goal.progress || 0), 0);
    return Math.round(totalProgress / currentPlan.goals.length);
  }, [currentPlan]);

  const completedGoals = useMemo(() => {
    return currentPlan?.goals?.filter(g => g.completed || g.status === 'completed').length || 0;
  }, [currentPlan]);

  const handleCreatePlan = () => {
    setPlanFormData({
      title: '',
      description: '',
      diagnosis: '',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: '',
      status: 'active',
      priority: 'normal',
    });
    setShowPlanDialog(true);
  };

  const handleSavePlan = () => {
    // Save plan logic
    const newPlan = {
      id: Date.now(),
      ...planFormData,
      goals: [],
      createdAt: new Date().toISOString(),
    };
    setPlans(prev => [...prev, newPlan]);
    setShowPlanDialog(false);
    setActivePlan(newPlan);
  };

  const handleCreateGoal = () => {
    setGoalFormData({
      title: '',
      description: '',
      targetDate: '',
      status: 'not_started',
      priority: 'normal',
      interventions: [],
      outcomeMeasures: '',
      progress: 0,
    });
    setEditingGoal(null);
    setShowGoalDialog(true);
  };

  const handleEditGoal = (goal) => {
    setGoalFormData(goal);
    setEditingGoal(goal);
    setShowGoalDialog(true);
  };

  const handleSaveGoal = () => {
    // Save goal logic
    const goal = {
      id: editingGoal?.id || Date.now(),
      ...goalFormData,
      completed: goalFormData.progress === 100 || goalFormData.status === 'completed',
    };

    if (editingGoal) {
      // Update existing goal
      const updatedPlan = {
        ...currentPlan,
        goals: currentPlan.goals.map(g => g.id === goal.id ? goal : g),
      };
      setPlans(prev => prev.map(p => p.id === currentPlan.id ? updatedPlan : p));
      setActivePlan(updatedPlan);
    } else {
      // Add new goal
      const updatedPlan = {
        ...currentPlan,
        goals: [...(currentPlan.goals || []), goal],
      };
      setPlans(prev => prev.map(p => p.id === currentPlan.id ? updatedPlan : p));
      setActivePlan(updatedPlan);
    }

    setShowGoalDialog(false);
    setEditingGoal(null);
  };

  const handleToggleGoalComplete = (goalId) => {
    const updatedPlan = {
      ...currentPlan,
      goals: currentPlan.goals.map(goal =>
        goal.id === goalId
          ? { ...goal, completed: !goal.completed, status: goal.completed ? 'in_progress' : 'completed', progress: goal.completed ? 90 : 100 }
          : goal
      ),
    };
    setPlans(prev => prev.map(p => p.id === currentPlan.id ? updatedPlan : p));
    setActivePlan(updatedPlan);
  };

  const handleUpdateProgress = (goalId, newProgress) => {
    const updatedPlan = {
      ...currentPlan,
      goals: currentPlan.goals.map(goal =>
        goal.id === goalId
          ? { ...goal, progress: Math.min(100, Math.max(0, newProgress)) }
          : goal
      ),
    };
    setPlans(prev => prev.map(p => p.id === currentPlan.id ? updatedPlan : p));
    setActivePlan(updatedPlan);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Treatment Plan
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive treatment planning and progress tracking
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {role === 'doctor' && (
            <>
              <Button variant="outlined" startIcon={<Add />} onClick={handleCreatePlan}>
                New Plan
              </Button>
              <Button variant="outlined" startIcon={<Download />}>
                Export
              </Button>
            </>
          )}
        </Box>
      </Box>

      {currentPlan ? (
        <Grid container spacing={3}>
          {/* Plan Overview */}
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <StyledCard>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        {currentPlan.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {currentPlan.description}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip
                          label={currentPlan.diagnosis}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={currentPlan.status}
                          size="small"
                          color={currentPlan.status === 'active' ? 'success' : 'default'}
                        />
                        <Chip
                          label={currentPlan.priority}
                          size="small"
                          color={
                            currentPlan.priority === 'high' ? 'error' :
                            currentPlan.priority === 'medium' ? 'warning' :
                            'default'
                          }
                        />
                      </Stack>
                    </Box>
                    {role === 'doctor' && (
                      <IconButton onClick={() => { setActivePlan(currentPlan); setShowPlanDialog(true); }}>
                        <Edit />
                      </IconButton>
                    )}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Overall Progress
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={overallProgress}
                          sx={{ height: 10, borderRadius: 5, mb: 1 }}
                        />
                        <Typography variant="h6" fontWeight={600}>
                          {overallProgress}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Goals Completed
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {completedGoals} / {currentPlan.goals?.length || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {currentPlan.goals?.length > 0 
                            ? Math.round((completedGoals / currentPlan.goals.length) * 100)
                            : 0}% completion rate
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Timeline
                        </Typography>
                        <Typography variant="body1">
                          {currentPlan.startDate && format(parseISO(currentPlan.startDate), 'MMM dd, yyyy')} -{' '}
                          {currentPlan.endDate
                            ? format(parseISO(currentPlan.endDate), 'MMM dd, yyyy')
                            : 'Ongoing'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </motion.div>
          </Grid>

          {/* Goals Section */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Treatment Goals
              </Typography>
              {role === 'doctor' && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleCreateGoal}
                >
                  Add Goal
                </Button>
              )}
            </Box>

            {currentPlan.goals && currentPlan.goals.length > 0 ? (
              <Grid container spacing={2}>
                {currentPlan.goals.map((goal, index) => (
                  <Grid item xs={12} key={goal.id}>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <GoalCard completed={goal.completed}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleGoalComplete(goal.id)}
                                disabled={role !== 'doctor'}
                              >
                                {goal.completed ? (
                                  <CheckCircle color="success" />
                                ) : (
                                  <RadioButtonUnchecked />
                                )}
                              </IconButton>
                              <Typography variant="h6" fontWeight={600}>
                                {goal.title}
                              </Typography>
                              <Chip
                                label={goal.status}
                                size="small"
                                color={
                                  goal.status === 'completed' ? 'success' :
                                  goal.status === 'in_progress' ? 'info' :
                                  'default'
                                }
                                sx={{ ml: 1 }}
                              />
                              <Chip
                                label={goal.priority}
                                size="small"
                                variant="outlined"
                                color={
                                  goal.priority === 'high' ? 'error' :
                                  goal.priority === 'medium' ? 'warning' :
                                  'default'
                                }
                                sx={{ ml: 1 }}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {goal.description}
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Progress
                                </Typography>
                                <Typography variant="caption" fontWeight={600}>
                                  {goal.progress}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={goal.progress}
                                color={goal.progress === 100 ? 'success' : 'primary'}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                            {goal.interventions && goal.interventions.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                  Interventions:
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                  {goal.interventions.map((intervention, idx) => (
                                    <Chip
                                      key={idx}
                                      label={intervention}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            )}
                            {goal.outcomeMeasures && (
                              <Typography variant="caption" color="text.secondary">
                                Outcome Measures: {goal.outcomeMeasures}
                              </Typography>
                            )}
                            {goal.targetDate && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Target Date: {format(parseISO(goal.targetDate), 'MMM dd, yyyy')}
                              </Typography>
                            )}
                          </Box>
                          {role === 'doctor' && (
                            <Box>
                              <IconButton size="small" onClick={() => handleEditGoal(goal)}>
                                <Edit />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                        {role === 'doctor' && (
                          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="caption">Update Progress:</Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={goal.progress}
                              onChange={(e) => handleUpdateProgress(goal.id, parseInt(e.target.value) || 0)}
                              inputProps={{ min: 0, max: 100 }}
                              sx={{ width: 100 }}
                            />
                            <Typography variant="caption">%</Typography>
                          </Box>
                        )}
                      </GoalCard>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No treatment goals set yet.
                </Typography>
                {role === 'doctor' && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleCreateGoal}
                    sx={{ mt: 2 }}
                  >
                    Create First Goal
                  </Button>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            No active treatment plan found.
          </Typography>
          {role === 'doctor' && (
            <Button variant="contained" startIcon={<Add />} onClick={handleCreatePlan}>
              Create Treatment Plan
            </Button>
          )}
        </Paper>
      )}

      {/* Plan Dialog */}
      <Dialog open={showPlanDialog} onClose={() => setShowPlanDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {activePlan ? 'Edit Treatment Plan' : 'Create New Treatment Plan'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Plan Title"
              value={planFormData.title}
              onChange={(e) => setPlanFormData({ ...planFormData, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={planFormData.description}
              onChange={(e) => setPlanFormData({ ...planFormData, description: e.target.value })}
            />
            <TextField
              fullWidth
              label="Diagnosis (ICD-10)"
              value={planFormData.diagnosis}
              onChange={(e) => setPlanFormData({ ...planFormData, diagnosis: e.target.value })}
              placeholder="e.g., F32.1 - Major Depressive Disorder, Single Episode, Moderate"
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={planFormData.startDate}
                  onChange={(e) => setPlanFormData({ ...planFormData, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date (Optional)"
                  type="date"
                  value={planFormData.endDate}
                  onChange={(e) => setPlanFormData({ ...planFormData, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={planFormData.status}
                label="Status"
                onChange={(e) => setPlanFormData({ ...planFormData, status: e.target.value })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
                <MenuItem value="discontinued">Discontinued</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={planFormData.priority}
                label="Priority"
                onChange={(e) => setPlanFormData({ ...planFormData, priority: e.target.value })}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPlanDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePlan}>
            {activePlan ? 'Update' : 'Create'} Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Goal Dialog */}
      <Dialog open={showGoalDialog} onClose={() => setShowGoalDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingGoal ? 'Edit Treatment Goal' : 'Create New Treatment Goal'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Goal Title"
              value={goalFormData.title}
              onChange={(e) => setGoalFormData({ ...goalFormData, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={goalFormData.description}
              onChange={(e) => setGoalFormData({ ...goalFormData, description: e.target.value })}
            />
            <TextField
              fullWidth
              label="Target Date"
              type="date"
              value={goalFormData.targetDate}
              onChange={(e) => setGoalFormData({ ...goalFormData, targetDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Outcome Measures"
              value={goalFormData.outcomeMeasures}
              onChange={(e) => setGoalFormData({ ...goalFormData, outcomeMeasures: e.target.value })}
              placeholder="e.g., PHQ-9 score < 10, Sleep diary shows improvement"
            />
            <TextField
              fullWidth
              label="Interventions (comma-separated)"
              value={goalFormData.interventions?.join(', ') || ''}
              onChange={(e) => setGoalFormData({
                ...goalFormData,
                interventions: e.target.value.split(',').map(i => i.trim()).filter(Boolean)
              })}
              placeholder="e.g., CBT sessions, Medication management, Exercise program"
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={goalFormData.status}
                    label="Status"
                    onChange={(e) => setGoalFormData({ ...goalFormData, status: e.target.value })}
                  >
                    <MenuItem value="not_started">Not Started</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={goalFormData.priority}
                    label="Priority"
                    onChange={(e) => setGoalFormData({ ...goalFormData, priority: e.target.value })}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box>
              <Typography variant="body2" gutterBottom>
                Progress: {goalFormData.progress}%
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={goalFormData.progress}
                onChange={(e) => setGoalFormData({
                  ...goalFormData,
                  progress: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                })}
                inputProps={{ min: 0, max: 100 }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGoalDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveGoal}>
            {editingGoal ? 'Update' : 'Create'} Goal
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedTreatmentPlan;

